import {
  Button,
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { useModalContractorDependenciesQuery } from "@src/__generated__/graphql";
import { observer } from "mobx-react-lite";
import type React from "react";
import type { FC } from "react";
import { ModalContractorForm } from "./ModalContractorForm";
import { useStore } from "./store";

interface ModalContractorFormProps {
  title: React.ReactNode;
  onClose?: () => void;
  primaryAction: {
    content?: string;
    onAction: () => void;
    loading?: boolean;
  };
  loading?: boolean;
}

const ModalContractor: FC<
  React.PropsWithChildren<ModalContractorFormProps>
> = ({ title, primaryAction, loading, children, onClose }) => {
  const store = useStore();
  const { form } = store;

  const { loading: loadingDependencies } = useModalContractorDependenciesQuery({
    onCompleted({ countrySimpleMap, accountingAccountsSimpleMap }) {
      if (countrySimpleMap) {
        form.setCountryOptions(countrySimpleMap);
      }
      if (accountingAccountsSimpleMap) {
        form.setAccountingAccountOptions(accountingAccountsSimpleMap);
      }
    },
  });

  const onCloseWrapper = () => {
    if (primaryAction.loading || loadingDependencies) return;
    onClose?.();
    store.toggleOpen(false);
  };

  return (
    <Modal
      isOpen={store.open}
      onClose={onCloseWrapper}
      size="2xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        {loading && (
          <Center
            pos="absolute"
            zIndex={100}
            top="0"
            right="0"
            bottom="0"
            left="0"
            bg="white"
          >
            <Spinner color="teal.500" size="lg" />
          </Center>
        )}
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <ModalContractorForm store={store} />
          {children}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={onCloseWrapper} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isLoading={primaryAction.loading}
            onClick={primaryAction.onAction}
          >
            {primaryAction.content ?? t`Save`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(ModalContractor);
